import { useLocale } from "next-intl";
import { getLocale } from "next-intl/server";

export {
  formatDate,
  formatDateWithFallback,
  type TDatePreset,
} from "libs/i18n/src/lib/dates";
import {
  formatDate,
  formatDateWithFallback,
  type TDatePreset,
} from "libs/i18n/src/lib/dates";
import { resolveLocale } from "libs/i18n/src/lib/locales";

/**
 * Client-side hook: returns a date formatting function bound to the
 * current locale. Accepts a named preset for the output style.
 *
 * @example
 * const formatDate = useFormatDate();
 * formatDate(new Date(), "full")  // "Monday, 29 June 2026" (en)
 * formatDate(new Date(), "full")  // "Montag, 29. Juni 2026" (de)
 */
export const useFormatDate = () => {
  const locale = resolveLocale(useLocale());
  return (
    date: Date | string | null | undefined,
    preset: TDatePreset,
    fallback?: string,
  ) => formatDateWithFallback(date, preset, locale, fallback);
};

/**
 * Server-side: returns a date formatting function bound to the
 * current locale. Await once, then call synchronously.
 *
 * @example
 * const formatDate = await getServerFormatDate();
 * formatDate("2026-06-29", "long") // "29 June 2026"
 */
export const getServerFormatDate = async () => {
  const locale = resolveLocale(await getLocale());
  return (
    date: Date | number | string,
    preset: TDatePreset,
  ) => formatDate(date, preset, locale);
};
